import React, { useEffect, useRef, useState, useCallback } from 'react';
import parse from 'html-react-parser';
import styled from '@emotion/styled';
import { Wrapper, Box } from '@qga/roo-ui/components';
import TextButton from 'components/TextButton';
import PropTypes from 'prop-types';
import Modal from 'components/Modal';
import { useModal } from 'lib/hooks';
import noop from 'lodash/noop';
import theme from 'lib/theme';

const TruncatedText = styled(Box)`
  @media screen {
    max-height: ${(props) => props.maxHeight};
    overflow: hidden;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      right: 0;
      left: 0;
      height: ${(props) => (props.isTruncated ? '3em' : '0')};
      background: ${(props) => props.background};
    }
  }

  @media print {
    max-height: inherit;
  }
`;
TruncatedText.displayName = 'TruncatedText';

const newLineToBr = (text) => text.replace(/\n+/g, '<br/>');

const TruncateText = ({ text, maxHeight, modalTitle, background, onClickReadMoreCallback, ...rest }) => {
  const { openModal, modalProps } = useModal();
  const [isTruncated, setIsTruncated] = useState(false);
  const parsedHtml = parse(newLineToBr(text));
  const truncatedTextRef = useRef(null);

  useEffect(() => {
    if (truncatedTextRef.current.scrollHeight !== truncatedTextRef.current.offsetHeight) {
      setIsTruncated(true);
    } else {
      setIsTruncated(false);
    }
  }, []);

  const openReadMoreModal = useCallback(() => {
    onClickReadMoreCallback();
    openModal();
  }, [onClickReadMoreCallback, openModal]);

  return (
    <Wrapper {...rest}>
      <TruncatedText
        fontSize={['sm', 'base']}
        ref={truncatedTextRef}
        isTruncated={isTruncated}
        maxHeight={maxHeight}
        background={background}
        data-testid="truncated-text"
      >
        {parsedHtml}
      </TruncatedText>
      {isTruncated && (
        <TextButton onClick={openReadMoreModal} data-print-style="hidden" data-testid="read-more-link">
          Read more
        </TextButton>
      )}

      <Modal {...modalProps} title={modalTitle}>
        <Box fontSize={['sm', 'base']}>{parsedHtml}</Box>
      </Modal>
    </Wrapper>
  );
};

TruncateText.propTypes = {
  text: PropTypes.string.isRequired,
  maxHeight: PropTypes.string,
  modalTitle: PropTypes.string,
  background: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onClickReadMoreCallback: PropTypes.func,
};

TruncateText.defaultProps = {
  maxHeight: '12em',
  modalTitle: '',
  background: theme.colors.gradients.transparentToWhite,
  onClickReadMoreCallback: noop,
};

export default TruncateText;
